/**
 * FunilPremium — as telas do Premium em sequência, com a lógica do lembrete
 * do teste e da compra. Peça única, usada por:
 *   • MonetizacaoHost (abrirFunilPremium): pós-intersticial e botões do app;
 *   • AssinaturaOffcanvas (Configurações › Assinatura, conta não pagante).
 *
 * Ordem (etapasDoFunil):
 *   inicio 'oferta'       oferta → lembrete* → comparativo → planos
 *                         (pós-intersticial)
 *   inicio 'comparativo'  comparativo → lembrete* → planos
 *                         (qualquer botão ou tela que leva ao Premium)
 *   * o lembrete só entra com teste grátis elegível E LEMBRETE_TESTE_ATIVO.
 *
 * Voltar anda uma etapa para trás, na mesma ordem. Na primeira etapa o topo
 * vira X e fecha (voltar do Android e Esc também).
 *
 * `contexto` = carregarContextoFunil() — quem monta o funil espera por ele,
 * para a primeira tela já nascer com o CTA certo (com ou sem teste grátis).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useUser } from '../../contexts/UserContext';
import { auth } from '../../firebaseConfig';
import { FCMService } from '../../services/notifications/FCMService';
import { abrirAjustes } from '../atividade/fonteGps';
import { esquecerEstadoAnuncios, lembreteTeste, registrarMetrica } from '../monetizacao/anunciosApi';
import { estadoAtual, marcarPremiumLocal } from '../monetizacao/monetizacao';
import { agendarReserva } from '../monetizacao/lembreteLocal';
import { verificarElegibilidadeTrial } from '../monetizacao/trial';
import { estadoLinkExterno } from '../monetizacao/linkExterno';
import { registrarEventoCriador } from '../criador/dispositivo';
import OfertaSemAnuncios from './OfertaSemAnuncios';
import LembreteTeste from './LembreteTeste';
import Comparativo from './Comparativo';
import PlanosAssinatura from './PlanosAssinatura';

// A loja pode demorar a responder a elegibilidade do teste (primeira leitura
// do offering). Passou disso, o funil abre SEM prometer teste — a tela de
// planos lê a loja de novo e mostra o teste se ele existir.
const ESPERA_TRIAL_MS = 5000;

function comTimeout(promessa, ms, valor) {
  return Promise.race([
    Promise.resolve(promessa).catch(() => valor),
    new Promise((resolve) => setTimeout(() => resolve(valor), ms)),
  ]);
}

/**
 * O que o funil precisa saber antes de abrir. Nunca lança.
 * @return {Promise<{trialElegivel:boolean, trialDias:number, lembreteAtivo:boolean,
 *   linkExterno:object}>}
 */
export async function carregarContextoFunil() {
  let cfg = {};
  try {
    const est = await estadoAtual();
    cfg = est?.cfg || {};
  } catch {
    cfg = {};
  }
  const trialElegivel = cfg.TRIAL_ATIVO
    ? await comTimeout(verificarElegibilidadeTrial(), ESPERA_TRIAL_MS, false)
    : false;
  return {
    trialElegivel: !!trialElegivel,
    trialDias: Number(cfg.TRIAL_DIAS) || 7,
    lembreteAtivo: !!cfg.LEMBRETE_TESTE_ATIVO,
    linkExterno: estadoLinkExterno(cfg),
  };
}

/**
 * Sequência de telas para um começo. Exportado para teste.
 * @param {{inicio?:string, trialElegivel?:boolean, lembreteAtivo?:boolean}} p
 * @return {string[]}
 */
export function etapasDoFunil({ inicio = 'oferta', trialElegivel = false, lembreteAtivo = false } = {}) {
  const lembrete = trialElegivel && lembreteAtivo ? ['lembrete'] : [];
  if (inicio === 'comparativo') return ['comparativo', ...lembrete, 'planos'];
  return ['oferta', ...lembrete, 'comparativo', 'planos'];
}

export default function FunilPremium({ inicio = 'oferta', origem, contexto, onFim }) {
  const { user, refreshUserData } = useUser() || {};
  const ctx = contexto || {};
  const etapas = etapasDoFunil({
    inicio,
    trialElegivel: ctx.trialElegivel,
    lembreteAtivo: ctx.lembreteAtivo,
  });

  const [etapa, setEtapa] = useState(() => (etapas.includes(inicio) ? inicio : etapas[0]));
  const escolhaRef = useRef(null); // diasAntes do lembrete, quando salvo
  const encerradoRef = useRef(false);
  const onFimRef = useRef(onFim);
  onFimRef.current = onFim;

  const posicao = etapas.indexOf(etapa);
  const anterior = posicao > 0 ? etapas[posicao - 1] : null;
  const proxima = posicao >= 0 ? etapas[posicao + 1] || null : null;

  // Métrica de sempre: toda vez que o Comparativo aparece (inclusive quando é
  // a primeira tela, no funil aberto por botão).
  useEffect(() => {
    if (etapa === 'comparativo') registrarMetrica('COMPARATIVO_VISTO');
  }, [etapa]);

  // Funil do criador que indicou esta conta ("paywall aberto"). Só quando a
  // PESSOA abriu (botão), como fazia a tela Premium antiga — a oferta que
  // aparece sozinha depois do anúncio não conta. Só quem entrou por código
  // tem AFFILIATE_APPLIED; o resto nem chama a API.
  // Uma vez por abertura do funil: o que interessa é o valor da montagem.
  const aberturaRef = useRef({ porBotao: inicio === 'comparativo', afiliado: !!user?.AFFILIATE_APPLIED, origem });
  useEffect(() => {
    const a = aberturaRef.current;
    if (a.porBotao && a.afiliado) {
      registrarEventoCriador(auth.currentUser, 'paywall_aberto', a.origem || 'premium');
    }
  }, []);

  const fechar = useCallback((comprou = false) => {
    if (encerradoRef.current) return;
    encerradoRef.current = true;
    if (!comprou && escolhaRef.current) {
      // Desistiu depois de escolher o lembrete: não fica PENDENTE à toa.
      lembreteTeste('DESCARTAR').catch(() => {});
    }
    escolhaRef.current = null;
    onFimRef.current?.(comprou);
  }, []);

  const ir = useCallback((alvo) => {
    if (alvo) setEtapa(alvo);
  }, []);

  const escolherLembrete = useCallback(async (diasAntes) => {
    if (Capacitor.isNativePlatform()) {
      let perm = await FCMService.permissionState().catch(() => 'prompt');
      if (perm === 'prompt' || perm === 'prompt-with-rationale') {
        perm = await FCMService.requestPermission('lembrete_teste');
      }
      if (perm !== 'granted' && perm !== 'unavailable') {
        registrarMetrica('LEMBRETE_PERMISSAO_NEGADA');
        return { permissaoNegada: true };
      }
    }
    try {
      const r = await lembreteTeste('SALVAR', { diasAntes, plataforma: Capacitor.getPlatform() });
      if (r?.salvo) escolhaRef.current = diasAntes;
    } catch {
      /* sem lembrete salvo o funil segue — o push é cortesia */
    }
    registrarMetrica('LEMBRETE_ESCOLHIDO');
    ir(proxima);
    return { ok: true };
  }, [ir, proxima]);

  const abrirAjustesNotificacao = useCallback(async () => {
    const r = await FCMService.requestFromSettings().catch(() => 'blocked');
    if (r !== 'granted') await abrirAjustes();
  }, []);

  const aoComprar = useCallback(async ({ emTeste } = {}) => {
    marcarPremiumLocal(); // anúncios somem na hora
    esquecerEstadoAnuncios();
    if (emTeste && escolhaRef.current) {
      try {
        // O servidor escolhe UM canal: PUSH (há token FCM ativo e o aparelho
        // permite) ou LOCAL (o aparelho agenda). Nunca os dois.
        const perm = await FCMService.permissionState().catch(() => 'denied');
        const r = await lembreteTeste('CONFIRMAR_APP', { pushPermitido: perm === 'granted' });
        if (r?.confirmado && r.canal === 'LOCAL') await agendarReserva(r);
      } catch {
        /* o webhook confirma de qualquer forma */
      }
      escolhaRef.current = null;
    }
    // O tier NÃO é escrito aqui: chega pelo SUBSCRIPTION_INFO (webhook). Só
    // pedimos o usuário de novo, como o paywall antigo fazia depois da compra.
    try {
      await refreshUserData?.();
    } catch {
      /* o listener do USER traz a mudança de qualquer forma */
    }
    fechar(true);
  }, [fechar, refreshUserData]);

  const voltar = anterior ? () => ir(anterior) : undefined;
  const fecharSemCompra = () => fechar(false);

  if (etapa === 'oferta') {
    return (
      <OfertaSemAnuncios
        trialElegivel={ctx.trialElegivel}
        trialDias={ctx.trialDias}
        onAceitar={() => {
          registrarMetrica('OFERTA_ACEITA');
          ir(proxima);
        }}
        onFechar={() => {
          registrarMetrica('OFERTA_RECUSADA');
          fechar(false);
        }}
      />
    );
  }
  if (etapa === 'lembrete') {
    return (
      <LembreteTeste
        trialDias={ctx.trialDias}
        onEscolher={escolherLembrete}
        onAbrirAjustes={abrirAjustesNotificacao}
        onFechar={fecharSemCompra}
        onVoltar={voltar}
      />
    );
  }
  if (etapa === 'comparativo') {
    return (
      <Comparativo
        trialElegivel={!!ctx.trialElegivel}
        trialDias={ctx.trialDias || 7}
        onContinuar={() => ir(proxima)}
        onFechar={fecharSemCompra}
        onVoltar={voltar}
      />
    );
  }
  return (
    <PlanosAssinatura
      origem={origem}
      onComprado={aoComprar}
      onFechar={fecharSemCompra}
      onVoltar={voltar}
      linkExterno={ctx.linkExterno}
    />
  );
}
